import { useCallback } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type { CheckoutSessionInput, InvoiceListParams } from './types';

/** Query-key factory — every billing key derives from here. */
export const billingKeys = {
  all: ['billing'] as const,
  subscription: () => [...billingKeys.all, 'subscription'] as const,
  invoiceLists: () => [...billingKeys.all, 'invoices'] as const,
  invoiceList: (params: InvoiceListParams) => [...billingKeys.invoiceLists(), params] as const,
};

/**
 * The billing read model, for any role.
 *
 * `staleTime: 0` deliberately: this query is the one thing on screen while a
 * school is locked or a checkout has just come back, and a stale "trial, 3 days
 * left" is exactly the number nobody should be shown.
 */
export function useSubscription(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => api.getSubscription(),
    staleTime: 0,
    enabled: options.enabled,
  });
}

/** Admin-only invoice history. */
export function useInvoices(params: InvoiceListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: billingKeys.invoiceList(params),
    queryFn: () => api.getInvoices(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

/**
 * Start a Stripe Checkout. The caller is responsible for the redirect — the
 * mutation only obtains the URL, so the navigation stays one testable seam
 * (`lib/redirect.ts`) instead of a side effect buried in a query hook.
 */
export function useCheckoutSession() {
  return useMutation({
    mutationFn: (input: CheckoutSessionInput) => api.createCheckoutSession(input),
  });
}

/** Open the Stripe Billing Portal. Same contract as checkout: URL only. */
export function usePortalSession() {
  return useMutation({
    mutationFn: () => api.createPortalSession(),
  });
}

/**
 * Re-read the subscription (and the invoices behind it) — used when the browser
 * lands back from a hosted Stripe page. The webhook is the source of truth, so
 * this refetch may legitimately still show the previous state for a moment;
 * the screen says so rather than pretending otherwise.
 */
export function useRefreshBilling() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: billingKeys.all });
  }, [qc]);
}
